if (!customElements.get('rm-oct-product-popup')) {
  customElements.define('rm-oct-product-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = document.getElementById(this.dataset.rootTitle)?.closest('.rm-oct');
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('img');
      if (!this.root || !this.image || typeof this.dialog?.showModal !== 'function') return;
      this.controller = new AbortController();
      this.controls = new Map();
      const options = { signal: this.controller.signal };
      this.enhanceControls();
      this.root.addEventListener('click', event => {
        if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = this.findTrigger(event.target);
        if (!trigger || this.failed) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      this.root.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const trigger = event.target.closest('[data-rm-oct-popup-control]');
        if (!trigger || (trigger.tagName === 'A' || trigger.tagName === 'BUTTON') || this.failed) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      const prepareImage = event => {
        if (this.findTrigger(event.target) && !this.failed) this.loadImage();
      };
      this.root.addEventListener('pointerover', event => {
        if (matchMedia('(hover: hover) and (pointer: fine)').matches) prepareImage(event);
      }, options);
      this.root.addEventListener('pointerdown', prepareImage, { ...options, passive: true });
      this.root.addEventListener('focusin', prepareImage, options);
      this.querySelector('[data-popup-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPress = event.target === this.dialog && this.isOutside(event);
      }, options);
      this.dialog.addEventListener('click', event => {
        if (this.backdropPress && event.target === this.dialog && this.isOutside(event)) this.close();
        this.backdropPress = false;
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        clearTimeout(this.closeTimer);
        cancelAnimationFrame(this.openFrame);
        this.dialog.classList.remove('is-open', 'is-closing');
        this.restorePage();
      }, options);
      this.image.addEventListener('error', () => {
        this.failed = true;
        this.close();
        this.restoreControls();
      }, options);
    }

    enhanceControls() {
      const attributes = ['role', 'tabindex', 'aria-label', 'aria-haspopup', 'aria-controls', 'data-rm-oct-popup-control', 'hidden'];
      this.root.querySelectorAll('.rm-oct__product, .rm-oct__example-item, [data-rm-oct-banner-trigger]').forEach(card => {
        const banner = card.matches('[data-rm-oct-banner-trigger]');
        if (!banner && !card.querySelector('.rm-oct__product-image img, .rm-oct__example-image img')) return;
        const control = banner ? card : card.querySelector('a[href]') || card.querySelector('.rm-oct__product-image, .rm-oct__example-image');
        this.controls.set(control, Object.fromEntries(attributes.map(name => [name, control.getAttribute(name)])));
        control.setAttribute('data-rm-oct-popup-control', '');
        control.setAttribute('aria-haspopup', 'dialog');
        control.setAttribute('aria-controls', this.dialog.id);
        if (banner) control.hidden = false;
        if (control.tagName !== 'A' && control.tagName !== 'BUTTON') {
          control.setAttribute('role', 'button');
          control.setAttribute('tabindex', '0');
          control.setAttribute('aria-label', card.dataset.rmOctProductLabel || 'View October offer');
        }
      });
    }

    restoreControls() {
      this.controls?.forEach((attributes, control) => {
        Object.entries(attributes).forEach(([name, value]) => {
          if (value === null) control.removeAttribute(name);
          else control.setAttribute(name, value);
        });
      });
      this.controls?.clear();
    }

    findTrigger(target) {
      const card = target.closest?.('.rm-oct__product, .rm-oct__example-item, [data-rm-oct-banner-trigger]');
      if (!card || !this.root.contains(card)) return null;
      return card.matches('[data-rm-oct-popup-control]') ? card : card.querySelector('[data-rm-oct-popup-control]');
    }

    loadImage() {
      if (this.image.getAttribute('src')) return;
      this.image.sizes = this.image.dataset.sizes;
      if (this.image.dataset.srcset) this.image.srcset = this.image.dataset.srcset;
      this.image.src = this.image.dataset.src;
    }

    open(trigger) {
      if (this.failed) return;
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      this.trigger = trigger;
      this.image.fetchPriority = 'high';
      this.loadImage();
      this.dialog.classList.remove('is-open', 'is-closing');
      if (!this.dialog.open) {
        const page = document.documentElement;
        this.previousOverflow = page.style.overflow;
        this.previousGutter = page.style.scrollbarGutter;
        this.locked = true;
        page.style.scrollbarGutter = 'stable';
        page.style.overflow = 'hidden';
        this.dialog.showModal();
      }
      void this.dialog.offsetWidth;
      this.openFrame = requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    }

    close() {
      if (!this.dialog.open) return;
      cancelAnimationFrame(this.openFrame);
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-open');
      this.dialog.classList.add('is-closing');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const closeMs = reduced ? 0 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--modal-close-dur')) || 150;
      if (!closeMs) this.finishClose();
      else this.closeTimer = setTimeout(() => this.finishClose(), closeMs);
    }

    finishClose() {
      clearTimeout(this.closeTimer);
      if (!this.dialog) return;
      this.dialog.classList.remove('is-open', 'is-closing');
      if (this.dialog.open) this.dialog.close();
      this.restorePage();
    }

    restorePage() {
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.style.overflow = this.previousOverflow;
      document.documentElement.style.scrollbarGutter = this.previousGutter;
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    isOutside(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.openFrame);
      this.controller?.abort();
      this.controller = null;
      this.finishClose();
      this.restoreControls();
    }
  });
}
